/**
 * LLM設定管理画面
 * 上部: LLM定義の CRUD
 * 下部: 機能別LLM割り当て管理
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLlms, createLlm, updateLlm, deleteLlm, toggleLlmActive,
  getLlmAssignments, updateLlmAssignment,
} from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Search, Loader2, Power, Save } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

// ==================== 割り当て型 ====================
interface LlmAssignment {
  id: number;
  purpose: string;
  label: string;
  llm_id: number | null;
  temperature: string | null;
  max_tokens: number | null;
  llm_provider: string | null;
  llm_model_name: string | null;
  llm_is_active: boolean | null;
}

interface LlmOption {
  id: number;
  provider: string;
  model_name: string;
  is_active: boolean;
}

// ==================== 割り当て行コンポーネント ====================
function AssignmentRow({
  assignment,
  llmOptions,
  onSave,
  isSaving,
}: {
  assignment: LlmAssignment;
  llmOptions: LlmOption[];
  onSave: (purpose: string, data: { llm_id: number | null; temperature: number | null; max_tokens: number | null }) => void;
  isSaving: boolean;
}) {
  const [llmId, setLlmId] = useState<number | null>(assignment.llm_id);
  const [temp, setTemp] = useState(assignment.temperature ?? '');
  const [tokens, setTokens] = useState(assignment.max_tokens?.toString() ?? '');

  const isDirty =
    llmId !== assignment.llm_id ||
    (temp || null) !== (assignment.temperature || null) ||
    (tokens || null) !== (assignment.max_tokens?.toString() || null);

  return (
    <TableRow className="border-b border-gray-100 hover:bg-gray-50/50">
      <TableCell className="py-4 text-base font-medium text-gray-900">{assignment.label}</TableCell>
      <TableCell className="py-4 text-base text-gray-500 font-mono">{assignment.purpose}</TableCell>
      <TableCell className="py-4">
        <select
          className="w-full px-2 py-1 border border-gray-300 rounded text-base"
          value={llmId ?? ''}
          onChange={(e) => setLlmId(e.target.value ? parseInt(e.target.value) : null)}
        >
          <option value="">-- デフォルト (env) --</option>
          {llmOptions.map((llm) => (
            <option key={llm.id} value={llm.id}>
              {llm.provider} / {llm.model_name} {!llm.is_active ? '(無効)' : ''}
            </option>
          ))}
        </select>
      </TableCell>
      <TableCell className="py-4">
        <Input
          className="w-20 text-base"
          type="number"
          step="0.01"
          min="0"
          max="2"
          placeholder="--"
          value={temp}
          onChange={(e) => setTemp(e.target.value)}
        />
      </TableCell>
      <TableCell className="py-4">
        <Input
          className="w-24 text-base"
          type="number"
          step="100"
          min="0"
          placeholder="--"
          value={tokens}
          onChange={(e) => setTokens(e.target.value)}
        />
      </TableCell>
      <TableCell className="py-4">
        <button
          type="button"
          disabled={!isDirty || isSaving}
          onClick={() =>
            onSave(assignment.purpose, {
              llm_id: llmId,
              temperature: temp ? parseFloat(temp) : null,
              max_tokens: tokens ? parseInt(tokens) : null,
            })
          }
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="保存"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        </button>
      </TableCell>
    </TableRow>
  );
}

// ==================== メインコンポーネント ====================
export default function LLMs() {
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLlm, setSelectedLlm] = useState<any>(null);
  const [formData, setFormData] = useState({
    provider: '',
    model_name: '',
    parameters: '{}',
    is_active: true,
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ---- LLM定義クエリ ----
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'llms', search, providerFilter, page],
    queryFn: () => getLlms({ search, provider: providerFilter || undefined, page, limit: 20 }),
  });

  // ---- 割り当てクエリ ----
  const { data: assignmentData, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['admin', 'llm-assignments'],
    queryFn: () => getLlmAssignments(),
  });

  // ---- LLM CRUD mutations ----
  const createMutation = useMutation({
    mutationFn: createLlm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llms'] });
      setIsCreateDialogOpen(false);
      setFormData({ provider: '', model_name: '', parameters: '{}', is_active: true });
      toast({ title: '成功', description: 'LLM設定を作成しました' });
    },
    onError: (error: Error) => {
      toast({ title: 'エラー', description: error.message || 'LLM設定の作成に失敗しました', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateLlm(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llms'] });
      setIsEditDialogOpen(false);
      setSelectedLlm(null);
      toast({ title: '成功', description: 'LLM設定を更新しました' });
    },
    onError: (error: Error) => {
      toast({ title: 'エラー', description: error.message || 'LLM設定の更新に失敗しました', variant: 'destructive' });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: toggleLlmActive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llms'] });
      toast({ title: '成功', description: 'LLM設定の状態を変更しました' });
    },
    onError: (error: Error) => {
      toast({ title: 'エラー', description: error.message || '状態変更に失敗しました', variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteLlm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llms'] });
      setIsDeleteDialogOpen(false);
      setSelectedLlm(null);
      toast({ title: '成功', description: 'LLM設定を削除しました' });
    },
    onError: (error: Error) => {
      toast({ title: 'エラー', description: error.message || 'LLM設定の削除に失敗しました', variant: 'destructive' });
    },
  });

  // ---- 割り当て mutation ----
  const assignmentMutation = useMutation({
    mutationFn: ({ purpose, data }: { purpose: string; data: any }) => updateLlmAssignment(purpose, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llm-assignments'] });
      toast({ title: '成功', description: 'LLM割り当てを更新しました' });
    },
    onError: (error: Error) => {
      toast({ title: 'エラー', description: error.message || '割り当ての更新に失敗しました', variant: 'destructive' });
    },
  });

  const handleCreate = () => {
    try {
      const parameters = JSON.parse(formData.parameters);
      createMutation.mutate({ ...formData, parameters });
    } catch {
      toast({ title: 'エラー', description: 'パラメータの形式が正しくありません', variant: 'destructive' });
    }
  };

  const handleEdit = (llm: any) => {
    setSelectedLlm(llm);
    setFormData({
      provider: llm.provider || '',
      model_name: llm.model_name || '',
      parameters: JSON.stringify(llm.parameters || {}, null, 2),
      is_active: llm.is_active !== false,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (selectedLlm) {
      try {
        const parameters = JSON.parse(formData.parameters);
        updateMutation.mutate({ id: selectedLlm.id, data: { ...formData, parameters } });
      } catch {
        toast({ title: 'エラー', description: 'パラメータの形式が正しくありません', variant: 'destructive' });
      }
    }
  };

  const handleSaveAssignment = (purpose: string, assignData: { llm_id: number | null; temperature: number | null; max_tokens: number | null }) => {
    assignmentMutation.mutate({ purpose, data: assignData });
  };

  // LLMオプション（割り当てドロップダウン用）
  const llmOptions: LlmOption[] = (data?.llms || []).map((l: any) => ({
    id: l.id,
    provider: l.provider,
    model_name: l.model_name,
    is_active: l.is_active,
  }));

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ==================== LLM定義セクション ==================== */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">LLM設定管理</h1>
            <p className="text-base text-gray-500 mt-2">LLMモデル定義と機能別割り当ての管理</p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
                <Plus className="w-4 h-4 mr-1.5" />
                新規LLM追加
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>新規LLM設定作成</DialogTitle>
                <DialogDescription>新しいLLM設定を追加します</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="provider">プロバイダー</Label>
                  <Input id="provider" value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })} placeholder="azure" />
                </div>
                <div>
                  <Label htmlFor="model_name">モデル名 / デプロイメント名</Label>
                  <Input id="model_name" value={formData.model_name}
                    onChange={(e) => setFormData({ ...formData, model_name: e.target.value })} placeholder="gpt-4o-mini" />
                </div>
                <div>
                  <Label htmlFor="parameters">パラメータ (JSON)</Label>
                  <textarea id="parameters"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm" rows={5}
                    value={formData.parameters}
                    onChange={(e) => setFormData({ ...formData, parameters: e.target.value })}
                    placeholder='{"temperature": 0.7, "max_tokens": 2000}' />
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="is_active" checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" />
                  <Label htmlFor="is_active">アクティブ</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>キャンセル</Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '作成'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* 検索・フィルタ */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="プロバイダーまたはモデル名で検索..."
                  value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-11 h-11 rounded-xl border-gray-200 bg-white" />
              </div>
            </div>
            <select
              className="h-11 px-4 pr-9 border border-gray-200 rounded-xl bg-white text-base font-medium text-gray-700 sm:w-64 appearance-none bg-no-repeat bg-[right_0.75rem_center]"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
              }}
              value={providerFilter}
              onChange={(e) => { setProviderFilter(e.target.value); setPage(1); }}>
              <option value="">すべてのプロバイダー</option>
              <option value="openai">OpenAI</option>
              <option value="azure">Azure</option>
            </select>
          </div>
        </div>

        {/* LLM一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">LLMモデル定義</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-200 hover:bg-transparent">
                    <TableHead className="text-base font-bold text-gray-700">ID</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">プロバイダー</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">モデル名</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">パラメータ</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">状態</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.llms?.map((llm: any) => (
                    <TableRow key={llm.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{llm.id}</TableCell>
                      <TableCell className="py-4 text-base font-medium text-gray-900">{llm.provider}</TableCell>
                      <TableCell className="py-4 text-base font-medium text-gray-900">{llm.model_name}</TableCell>
                      <TableCell className="py-4 max-w-xs truncate text-base text-gray-700">
                        {JSON.stringify(llm.parameters || {})}
                      </TableCell>
                      <TableCell className="py-4">
                        <span className={`inline-flex w-24 items-center justify-center rounded-full px-3 py-1 text-base font-bold ${llm.is_active ? 'border border-green-300 bg-green-50 text-green-700' : 'border border-gray-300 bg-gray-50 text-gray-700'}`}>
                          {llm.is_active ? 'アクティブ' : '無効'}
                        </span>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => toggleMutation.mutate(llm.id)}
                            disabled={toggleMutation.isPending}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                            aria-label="状態切替"
                          >
                            <Power className={`w-4 h-4 ${llm.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEdit(llm)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                            aria-label="編集"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => { setSelectedLlm(llm); setIsDeleteDialogOpen(true); }}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                            aria-label="削除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data?.pagination && (
                <div className="flex justify-between items-center mt-5 pt-4">
                  <div className="text-base text-gray-700">
                    全 {data.pagination.total} 件中 {(page - 1) * 20 + 1} - {Math.min(page * 20, data.pagination.total)} 件
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1} className="h-9 rounded-lg border-gray-200">前へ</Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= data.pagination.total} className="h-9 rounded-lg border-gray-200">次へ</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ==================== 機能別LLM割り当てセクション ==================== */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-2">機能別LLM割り当て</div>
          <p className="text-base text-gray-500 mb-4">
            各機能にどのLLMを使用するかを設定します。「デフォルト (env)」は環境変数の設定を使用します。
          </p>
          {assignmentsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 hover:bg-transparent">
                  <TableHead className="text-base font-bold text-gray-700">機能</TableHead>
                  <TableHead className="text-base font-bold text-gray-700">キー</TableHead>
                  <TableHead className="text-base font-bold text-gray-700">LLM</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 w-24">Temperature</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 w-28">Max Tokens</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 w-16">保存</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(assignmentData?.assignments || []).map((a: LlmAssignment) => (
                  <AssignmentRow
                    key={a.purpose}
                    assignment={a}
                    llmOptions={llmOptions}
                    onSave={handleSaveAssignment}
                    isSaving={assignmentMutation.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* 編集ダイアログ */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>LLM設定編集</DialogTitle>
              <DialogDescription>LLM設定を編集します</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-provider">プロバイダー</Label>
                <Input id="edit-provider" value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-model_name">モデル名</Label>
                <Input id="edit-model_name" value={formData.model_name}
                  onChange={(e) => setFormData({ ...formData, model_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-parameters">パラメータ (JSON)</Label>
                <textarea id="edit-parameters"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm" rows={5}
                  value={formData.parameters}
                  onChange={(e) => setFormData({ ...formData, parameters: e.target.value })} />
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="edit-is_active" checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" />
                <Label htmlFor="edit-is_active">アクティブ</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>キャンセル</Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '更新'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>LLM設定を削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。LLM設定「{selectedLlm?.provider} / {selectedLlm?.model_name}」を削除しますか？
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={() => selectedLlm && deleteMutation.mutate(selectedLlm.id)}
                disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
